/**
 * MODULE: domain/templates/frontend/api/templatesApi.ts
 * PURPOSE: API client for template generation endpoints
 * DEPENDENCIES: @shared/lib/frontend/api/config (getAuthHeaders)
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: 325 / 350 lines
 * RECENT: 2026-05-13 [2.2.964] — Opaque hub ids: rely on server **404** for **`getById`** / **`export`**; client skips UUID-only calls when **`isTenantTemplateUuid`** is false — root **`CHANGELOG` [2.2.964]`**. 2026-04-19 [2.2.532] — **`delete(id)`** single-row soft-hide (same metadata as **`deleteMany`**) for hub row confirm — root **`CHANGELOG` [2.2.532]**. [2.2.530] — **`exportBatch`** **`fetch`** POST **`/export-batch`** → **`blob()`**. **`search({ q, limit, signal })`** command palette. [2.2.158] **`API_CONFIG.baseURL`**. [2.2.157] **`getAuthHeaders()`**.
 */

import { API_CONFIG, getAuthHeaders } from '@shared/lib/frontend/api/config';
import type {
  GeneratePresentationRequest,
  GenerateEmailRequest,
  CreateEmailTemplateRequest,
  GenerateEventRequest,
  GenerateSalesGuideRequest,
  GenerateTemplateResponse,
  TemplateListResponse,
  TemplateFilters,
  Template,
  TemplatePatchRequest,
  RefineTemplateRequest,
  MultiModalGenerationRequest,
  TemplateVersionListResponse,
  TemplateVersion,
  ExportTemplateRequest,
  ExportTemplateResponse,
  ExportFormat,
  ShareTemplateRequest,
  ShareTemplateResponse,
  TemplateAnalytics,
  DuplicateTemplateDraftResponse,
  PlaybookSectionDuplicateRequest,
} from '../../contracts/templates.types';

// Use same API origin as other clients (VITE_API_BASE_URL on Azure → :8080). Relative /api/... hits :80 nginx only if proxied.
const BASE_URL = `${API_CONFIG.baseURL.replace(/\/$/, '')}/api/v1/templates`;

async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export const templatesApi = {
  generatePresentation: async (
    request: GeneratePresentationRequest
  ): Promise<GenerateTemplateResponse> => {
    return fetchWithAuth<GenerateTemplateResponse>(
      `${BASE_URL}/presentations/generate`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  },

  generateEmail: async (
    request: GenerateEmailRequest
  ): Promise<GenerateTemplateResponse> => {
    return fetchWithAuth<GenerateTemplateResponse>(
      `${BASE_URL}/emails/generate`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  },

  /** Manual compose — same tenant templates table as list/get (golden path). */
  createEmail: async (request: CreateEmailTemplateRequest): Promise<Template> => {
    return fetchWithAuth<Template>(`${BASE_URL}/emails`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  },

  generateEvent: async (
    request: GenerateEventRequest
  ): Promise<GenerateTemplateResponse> => {
    return fetchWithAuth<GenerateTemplateResponse>(
      `${BASE_URL}/events/generate`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  },

  generateSalesGuide: async (
    request: GenerateSalesGuideRequest
  ): Promise<GenerateTemplateResponse> => {
    return fetchWithAuth<GenerateTemplateResponse>(
      `${BASE_URL}/sales-guides/generate`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  },

  list: async (filters?: TemplateFilters): Promise<TemplateListResponse> => {
    const params = new URLSearchParams();

    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.page_size) params.append('page_size', filters.page_size.toString());

    const url = `${BASE_URL}${params.toString() ? '?' + params.toString() : ''}`;
    return fetchWithAuth<TemplateListResponse>(url);
  },

  /** Server-side title and prompt search — used by global command palette (Sales Hub content). */
  search: async (params: {
    q: string;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<Template[]> => {
    const limit = params.limit ?? 8;
    const q = params.q.trim();
    const sp = new URLSearchParams();
    sp.set('search', q);
    sp.set('page_size', String(limit));
    sp.set('page', '1');
    const url = `${BASE_URL}?${sp.toString()}`;
    const res = await fetchWithAuth<TemplateListResponse>(url, { signal: params.signal });
    return res.data ?? [];
  },

  get: async (id: string): Promise<Template> => {
    return fetchWithAuth<Template>(`${BASE_URL}/${id}`);
  },

  patch: async (id: string, body: TemplatePatchRequest): Promise<Template> => {
    return fetchWithAuth<Template>(`${BASE_URL}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /** Merge tags into each template's metadata (Sales Hub library bulk tag). */
  tag: async (ids: string[], tags: string[], currentTagsById: Record<string, string[]>): Promise<void> => {
    await Promise.all(
      ids.map(async (id) => {
        const cur = currentTagsById[id] ?? [];
        const merged = Array.from(new Set([...cur, ...tags]));
        await fetchWithAuth<Template>(`${BASE_URL}/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify({ metadata: { hub_library_tags: merged } }),
        });
      })
    );
  },

  /** Soft-hide rows in the hub library (metadata); list filters these out client-side until restored. */
  deleteMany: async (ids: string[]): Promise<void> => {
    await Promise.all(
      ids.map((id) =>
        fetchWithAuth<Template>(`${BASE_URL}/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify({ metadata: { hub_library_hidden: true } }),
        })
      )
    );
  },

  /** Single-row soft-hide (same semantics as **`deleteMany`**); hub row kebab and tests call this. */
  delete: async (id: string): Promise<void> => {
    await fetchWithAuth<Template>(`${BASE_URL}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ metadata: { hub_library_hidden: true } }),
    });
  },

  restoreMany: async (ids: string[]): Promise<void> => {
    await Promise.all(
      ids.map((id) =>
        fetchWithAuth<Template>(`${BASE_URL}/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify({ metadata: { hub_library_hidden: false } }),
        })
      )
    );
  },

  /**
   * Batch zip export: server builds Markdown previews per id (no jszip in the browser bundle).
   */
  exportBatch: async (ids: string[], format: ExportFormat): Promise<Blob> => {
    const response = await fetch(`${BASE_URL}/export-batch`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ ids, format }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }));
      throw new Error((error as { detail?: string }).detail || `HTTP ${response.status}`);
    }
    return response.blob();
  },

  duplicate: async (templateId: string): Promise<DuplicateTemplateDraftResponse> => {
    return fetchWithAuth<DuplicateTemplateDraftResponse>(
      `${BASE_URL}/${encodeURIComponent(templateId)}/duplicate`,
      { method: 'POST' }
    );
  },

  /** Duplicate a hub playbook section into a tenant draft sales_guide (catalogue sections are client-known ids). */
  duplicateSection: async (
    sectionId: string,
    body: PlaybookSectionDuplicateRequest
  ): Promise<DuplicateTemplateDraftResponse> => {
    return fetchWithAuth<DuplicateTemplateDraftResponse>(
      `${BASE_URL}/playbook-sections/${encodeURIComponent(sectionId)}/duplicate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );
  },

  // ==========================================================================
  // Refinement and Versioning
  // ==========================================================================

  refine: async (
    id: string,
    request: RefineTemplateRequest
  ): Promise<Template> => {
    return fetchWithAuth<Template>(`${BASE_URL}/${id}/refine`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  generateMultiModal: async (
    request: MultiModalGenerationRequest
  ): Promise<GenerateTemplateResponse> => {
    return fetchWithAuth<GenerateTemplateResponse>(
      `${BASE_URL}/presentations/generate-multimodal`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  },

  listVersions: async (id: string): Promise<TemplateVersionListResponse> => {
    return fetchWithAuth<TemplateVersionListResponse>(
      `${BASE_URL}/${id}/versions`
    );
  },

  getVersion: async (id: string, version: number): Promise<TemplateVersion> => {
    return fetchWithAuth<TemplateVersion>(
      `${BASE_URL}/${id}/versions/${version}`
    );
  },

  restoreVersion: async (id: string, version: number): Promise<Template> => {
    return fetchWithAuth<Template>(
      `${BASE_URL}/${id}/versions/${version}/restore`,
      { method: 'POST' }
    );
  },

  // ==========================================================================
  // Export, Sharing, and Analytics
  // ==========================================================================

  export: async (
    id: string,
    request: ExportTemplateRequest
  ): Promise<ExportTemplateResponse> => {
    return fetchWithAuth<ExportTemplateResponse>(`${BASE_URL}/${id}/export`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  share: async (
    id: string,
    request: ShareTemplateRequest
  ): Promise<ShareTemplateResponse> => {
    return fetchWithAuth<ShareTemplateResponse>(`${BASE_URL}/${id}/share`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  revokeShare: async (id: string, shareId: string): Promise<void> => {
    await fetchWithAuth<{ success: boolean }>(
      `${BASE_URL}/${id}/share/${shareId}`,
      { method: 'DELETE' }
    );
  },

  getAnalytics: async (id: string): Promise<TemplateAnalytics> => {
    return fetchWithAuth<TemplateAnalytics>(`${BASE_URL}/${id}/analytics`);
  },

  trackEvent: async (
    id: string,
    eventType: 'view' | 'use' | 'export' | 'share'
  ): Promise<void> => {
    await fetchWithAuth<{ success: boolean }>(
      `${BASE_URL}/${id}/analytics/track?event_type=${eventType}`,
      { method: 'POST' }
    );
  },
};
